import {formatRateParts} from "core/format/byteFormat";
import type {RecipientActivity} from "core/p2p/peerActivity";
import {useLocale, useMessages} from "i18n";
import {recipientsPace, recipientsRing, RingKind} from "./liveRecipientRules";
import {GaugeDetail, GaugeSvg} from "./ProgressCard.styles";
import {ArcFill, ArcTrack, RingBox, RingCenter, Speed} from "./RecipientsRing.styles";

/** One arc of the turn, drawn as a dash along the whole ring path: `length` long, starting `start` along it. */
function dash(start: number, length: number) {
	return {strokeDasharray: `${length} 1`, strokeDashoffset: -start};
}

/**
 * The ring above the live card's recipient list (kitecorp/transferify-ui#3320, the prototype's variant B): one
 * equal arc per recipient, clockwise from 12 o'clock in list order, each filled with that recipient's progress,
 * and past six one fill for everyone (`./liveRecipientRules`). The centre says how fast the downloading recipients go
 * together and how many they are. The arcs are drawing only: the rows under the ring state each recipient's
 * figures in words.
 */
export function RecipientsRing({ordered}: {ordered: readonly RecipientActivity[]}) {
	const {sender, peerConnection} = useMessages();
	const {locale} = useLocale();
	const ring = recipientsRing(ordered);
	const pace = recipientsPace(ordered);
	const rate = formatRateParts(pace.bytesPerSecond, locale);
	return (
		<RingBox>
			<GaugeSvg aria-hidden="true">
				{ring.kind === RingKind.Segmented ? ring.arcs.map((arc, index) => (
					<g key={ordered[index].number}>
						<ArcTrack pathLength={1} style={dash(arc.start, arc.length)}/>
						{arc.fill > 0 && <ArcFill pathLength={1} style={dash(arc.start, arc.fill)}/>}
					</g>
				)) : (
					<>
						<ArcTrack/>
						{ring.fraction > 0 && <ArcFill pathLength={1} style={dash(0, ring.fraction)}/>}
					</>
				)}
			</GaugeSvg>
			{/* Drawing too: each row states its own speed, and the list's status line says how many are downloading. */}
			<RingCenter aria-hidden="true">
				<Speed><b>{rate.value}</b><span>{peerConnection.speed(rate.unit)}</span></Speed>
				<GaugeDetail>{sender.instant.downloadingNow(pace.downloading)}</GaugeDetail>
			</RingCenter>
		</RingBox>
	);
}
